import { createRoute, type AnyRoute } from "@tanstack/react-router";
import { PackagePlus } from "lucide-react";

import type { FeatureModule } from "@/features/types";
import { guarded } from "@/lib/auth";

import type { SortKey } from "./lib";
import { UpsellOpsPage } from "./pages/UpsellOpsPage";

/** Search params on /upsell-ops. Filters + sort live in the URL so a filtered
 *  view is shareable; `account` (+ `expanded`) drive the account drawer, same
 *  convention as Customer Tiers. */
export interface UpsellOpsSearch {
  tab?: "dashboard" | "config";
  q?: string;
  /** Comma list of tiers, e.g. "1,2". */
  tiers?: string;
  csm?: string;
  /** Group key from a KPI-card click — show only customers missing it. */
  missing?: string;
  legacy?: boolean;
  open?: boolean;
  sort?: SortKey;
  account?: number;
  expanded?: boolean;
}

const SORTS: SortKey[] = ["op", "name", "newest", "tier", "arr"];

function validateUpsellOpsSearch(raw: Record<string, unknown>): UpsellOpsSearch {
  const out: UpsellOpsSearch = {};
  if (raw.tab === "dashboard" || raw.tab === "config") out.tab = raw.tab;
  if (typeof raw.q === "string" && raw.q) out.q = raw.q;
  if (typeof raw.tiers === "string" && raw.tiers) out.tiers = raw.tiers;
  if (typeof raw.csm === "string" && raw.csm) out.csm = raw.csm;
  if (typeof raw.missing === "string" && raw.missing) out.missing = raw.missing;
  if (raw.legacy === true || raw.legacy === "1" || raw.legacy === "true") out.legacy = true;
  if (raw.open === true || raw.open === "1" || raw.open === "true") out.open = true;
  if (SORTS.includes(raw.sort as SortKey)) out.sort = raw.sort as SortKey;
  if (raw.account !== undefined && raw.account !== "") {
    const n = Number(raw.account);
    if (Number.isFinite(n)) out.account = n;
  }
  if (raw.expanded === true || raw.expanded === "1" || raw.expanded === "true") {
    out.expanded = true;
  }
  return out;
}

export const upsellOpsModule: FeatureModule = {
  key: "upsell_ops",
  nav: {
    label: "Upsell Ops",
    icon: PackagePlus,
    to: "/upsell-ops",
    requires: "upsell_ops.read",
    order: 40,
    group: "Revenue",
  },
  routes: (rootRoute) => [
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/upsell-ops",
      component: guarded("upsell_ops.read", UpsellOpsPage),
      validateSearch: validateUpsellOpsSearch,
    }),
  ] as unknown as AnyRoute[],
};
